import type { ColDef, GridApi, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Download } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { useApi } from "../../api";
import type { PortalConfig } from "../../config";
import { theme } from "../TicketsPage";
import { HBars, prettyDay, RangePicker, Tile, usd, useRange, type Range } from "./common";

interface Expense {
  id: string; day: string; ticketNo: number; customer: string; watchmaker: string | null; status: string;
  supplier: string | null; category: string | null; description: string | null; amount: number;
}

/**
 * Expenses (owner, 2026-11-04; old Expenses page): every expense added to a ticket in the range, with totals by
 * category and by supplier. The totals follow the grid's filters.
 */
export function ExpensesPage({ config }: { config: PortalConfig }) {
  const [range, setRange] = useRange();
  const { data, error } = useApi<{ range: Range; rows: Expense[] }>(config, `/reports/expenses?from=${range.from}&to=${range.to}`);
  const grid = useRef<GridApi<Expense> | null>(null);
  const [shown, setShown] = useState<Expense[] | null>(null);
  const rows = shown ?? data?.rows ?? [];
  const total = rows.reduce((s, e) => s + e.amount, 0);
  // Grouped without regard to capitals or spaces (old entries have "Ebay" and "EBAY"); shown as first written.
  const by = (k: "watchmaker" | "supplier") => {
    const groups = new Map<string, { label: string; value: number }>();
    for (const e of rows) {
      const label = e[k]?.trim().replace(/\s+/g, " ") || "(none)";
      const key = label.toLowerCase();
      const g = groups.get(key) ?? { label, value: 0 };
      g.value += e.amount; groups.set(key, g);
    }
    return [...groups.values()].sort((a, b) => b.value - a.value).slice(0, 10);
  };

  const columns = useMemo<ColDef<Expense>[]>(() => [
    { field: "day", headerName: "Added", width: 150, sort: "desc", valueFormatter: (p) => prettyDay(String(p.value)) },
    { field: "ticketNo", headerName: "Ticket", width: 100, filter: "agNumberColumnFilter",
      cellRenderer: (p: ICellRendererParams<Expense>) => p.data ? <Link to={`/tickets/${p.data.ticketNo}`}>{p.data.ticketNo}</Link> : null },
    { field: "customer", headerName: "Customer", width: 160 },
    { field: "watchmaker", headerName: "Watchmaker", width: 150 },
    { field: "supplier", headerName: "Supplier", width: 150 },
    { field: "category", headerName: "Category", width: 140, hide: true },
    { field: "description", headerName: "Description", flex: 1, minWidth: 200 },
    { field: "amount", headerName: "Amount", width: 120, type: "rightAligned", filter: "agNumberColumnFilter", valueFormatter: (p) => usd(Number(p.value)) },
    { field: "status", headerName: "Ticket status", width: 160, hide: true },
  ], []);

  return (
    <div className="report-page">
      <div className="report-head"><h2>Expenses</h2></div>
      <RangePicker range={range} onChange={(r) => { setShown(null); setRange(r); }}>
        <button type="button" className="tb-btn" disabled={!data?.rows.length} onClick={() => grid.current?.exportDataAsCsv({ fileName: `expenses-${range.from}-to-${range.to}.csv` })}>
          <Download size={15} />CSV</button>
      </RangePicker>
      {error && <p className="error">{error.message}</p>}
      <div className="tiles">
        <Tile label="Total expenses" value={total} money hint="Follows the grid's filters" />
        <Tile label="Expenses" value={rows.length} />
        <Tile label="Tickets" value={new Set(rows.map((e) => e.ticketNo)).size} />
      </div>
      <div className="report-split">
        <div className="grid-box report-grid">
          <AgGridReact<Expense> theme={theme} rowData={data ? data.rows : null} columnDefs={columns} getRowId={(p) => p.data.id}
            defaultColDef={{ sortable: true, filter: "agTextColumnFilter", resizable: true }}
            onGridReady={(e) => { grid.current = e.api; }}
            onFilterChanged={(e) => { const list: Expense[] = []; e.api.forEachNodeAfterFilter((n) => { if (n.data) list.push(n.data); }); setShown(list); }}
            onRowDataUpdated={() => setShown(null)}
            pagination paginationPageSize={100} overlayNoRowsTemplate="No expenses in this range." loading={!data && !error} />
        </div>
        <aside className="report-side">
          <section className="panel"><h3>By watchmaker</h3><HBars rows={by("watchmaker")} format={(n) => usd(n, false)} label="Expenses by watchmaker" /></section>
          <section className="panel"><h3>By supplier</h3><HBars rows={by("supplier")} format={(n) => usd(n, false)} label="Expenses by supplier" /></section>
        </aside>
      </div>
    </div>
  );
}
